<template>
  <div class="overflow-scroll-table">
    <table class="responsive-table">
      <thead>
        <tr>
          <th v-for="col in columns" :key="col.id" @click="onSort(col.id)">
            {{ col.display_label }}
            <font-awesome-icon :icon="['fas', 'sort']" class="icon alt" />
            <span
              v-if="col.id === sortedColumn"
              :class="sortedAsc ? 'active-up' : 'active-down'"
            >
              <font-awesome-icon
                :icon="['fas', 'sort-down']"
                class="icon alt"
              />
              <font-awesome-icon :icon="['fas', 'sort-up']" class="icon alt" />
            </span>
          </th>
          <th />
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in students" :key="row.id">
          <td>
            {{ row.name }}
            <label class="d-block d-md-none"> Student: </label>
          </td>

          <td>
            {{ row.email }}
            <label class="d-block d-md-none"> Email: </label>
          </td>

          <td>
            {{ row.school_name || 'N/A' }}
            <label class="d-block d-md-none"> College: </label>
          </td>

          <td>
            <template v-if="row.state_name">{{ row.state_name }}</template>
            <template v-else>(No location)</template>
            <label class="d-block d-md-none"> State: </label>
          </td>

          <td>
            {{ row.date_joined | dateOnly }}
            <label class="d-block d-md-none"> Date Joined: </label>
          </td>

          <td>
            <span v-if="row.last_login">{{ row.last_login | dateOnly }}</span>
            <span v-else>N/A</span>
            <label class="d-block d-md-none"> Last Login: </label>
          </td>

          <td class="icons">
            <button
              v-b-tooltip.hover.top="'Impersonate User'"
              type="button"
              class="fa-btn"
              title="View as User"
              @click="$emit('impersonate', row.id)"
            >
              <font-awesome-icon
                :icon="['fas', 'user-secret']"
                class="icon alt"
              />
            </button>

            <button
              v-b-tooltip.hover.top="'Send Message'"
              type="button"
              class="fa-btn"
              title="Send Message"
              @click="sendMessage({ student: row })"
            >
              <font-awesome-icon :icon="['fas', 'envelope']" class="icon alt" />
            </button>

            <button
              v-b-tooltip.hover.top="'View Student Details'"
              type="button"
              class="fa-btn"
              title="View Student Details"
              @click="viewStudent(row)"
            >
              <font-awesome-icon :icon="['fas', 'file-alt']" class="icon alt" />
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
export default {
  name: 'SuperAdminStudentTable',

  props: {
    students: {
      type: Array,
      required: true
    },

    sortedColumn: {
      type: String,
      required: true
    },
    sortedAsc: {
      type: Boolean,
      required: true
    },
    onSort: {
      type: Function,
      required: true
    },

    sendMessage: {
      type: Function,
      required: true
    },
    viewStudent: {
      type: Function,
      required: true
    }
  },

  data() {
    return {
      columns: [
        { id: 'last_name', display_label: 'Student' },
        { id: 'email', display_label: 'Email' },
        { id: 'school_name', display_label: 'College' },
        { id: 'state_name', display_label: 'State' },
        { id: 'date_joined', display_label: 'Date Joined' },
        { id: 'last_login', display_label: 'Last Login' }
      ]
    };
  }
};
</script>

<style lang="scss" scoped>
.overflow-scroll-table {
  max-width: 100%;
  overflow-x: auto;
}

.responsive-table {
  @include border-radius($border-radius);

  width: 100%;
  border: none;

  thead {
    background: none;
    color: white;

    th {
      position: relative;
      padding: 0.75rem;
      padding-right: $gutter;
      border-bottom: 2px solid rgba(255, 255, 255, 0.25);
      cursor: pointer;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;

      i,
      svg {
        position: absolute;
        top: 17px;
        right: 10px;
      }

      .fa-sort {
        z-index: 1;
        opacity: 0.2;
      }

      span {
        .fa-sort-up {
          z-index: 2;
          opacity: 0;
        }

        .fa-sort-down {
          z-index: 3;
          opacity: 0;
        }

        &.active-down {
          .fa-sort-down {
            opacity: 1;
          }
        }

        &.active-up {
          .fa-sort-up {
            opacity: 1;
          }
        }
      }

      &:hover {
        .fa-sort {
          opacity: 0.3;
        }

        span {
          .fa-sort-down {
            opacity: 1;
          }

          &.active-down {
            .fa-sort-down {
              opacity: 1;
            }

            .fa-sort-up {
              opacity: 0;
            }
          }

          &.active-up {
            .fa-sort-up {
              opacity: 1;
            }

            .fa-sort-down {
              opacity: 0;
            }
          }
        }
      }
    }
  }

  tbody {
    tr {
      td {
        padding: 0.75rem;
        color: rgba(white, 0.87);
        border-top: 1px solid rgba(255, 255, 255, 0.25);

        a {
          color: darken($highlight-color, 5%);
        }
      }
    }
  }

  .actions {
    text-align: right;
  }

  @media (max-width: $sm-max) {
    display: block;
    border: none;

    thead,
    tbody,
    th,
    td,
    tr {
      display: block;
    }

    tbody {
      tr {
        background: $dark-blue;

        td {
          padding: 0.75rem;
          text-align: right;
          border-bottom: none;
          border-top: 1px solid rgba(255, 255, 255, 0.25);

          &:last-of-type {
            border-bottom: none;
          }
        }
      }
    }

    thead tr {
      position: absolute;
      top: -9999px;
      left: -9999px;
    }

    tr {
      @include border-radius($border-radius);

      margin: 0 0 1rem 0;
      background: white;
    }

    td {
      position: relative;
      padding-left: 50%;
      border: none;
      border-bottom: $secondary-border;

      label {
        position: absolute;
        top: 12.5px;
        left: 15px;
        width: 45%;
        padding-right: 10px;
        font-weight: bold;
        white-space: nowrap;
        text-align: left;
      }
    }

    .btn-ghost {
      border-color: $primary-color;
      background: $primary-color;
      color: $btn-color;
      height: 45px;

      &:hover,
      &:focus {
        border-color: darken($primary-color, 5%);
        background-color: darken($primary-color, 5%);
        color: $btn-color;
      }
    }
  }

  .icons {
    color: $green;
    text-align: right;
  }
}

.fa-btn {
  background: none;
  color: white;
  border: none;
  box-shadow: none;
}
</style>
